import { useMutation } from '@tanstack/react-query';
import { orpc } from '@/api/rpc/client';
import type { ExportFormat } from '@erp/shared-types';

function triggerDownload(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function downloadBase64PDF(base64: string, filename: string) {
  const byteString = atob(base64);
  const bytes = new Uint8Array(byteString.length);
  for (let i = 0; i < byteString.length; i++) {
    bytes[i] = byteString.charCodeAt(i);
  }
  triggerDownload(new Blob([bytes], { type: 'application/pdf' }), filename);
}

function sanitizeFilenamePart(value: string): string {
  return value
    .trim()
    .replace(/[<>:"/\\|?*]/g, '-')
    .replace(/[. ]+$/g, '');
}

export function useGenerateAuditReport() {
  return useMutation(
    orpc.auditReports.generate.mutationOptions({
      onSuccess: (data) => {
        const dateStr = new Date().toISOString().slice(0, 10);
        const module = data.module;

        const filterParts: string[] = [];
        const soNums = data.filters?.salesOrderNumbers;
        const poNums = data.filters?.purchaseOrderNumbers;
        if (soNums?.length === 1) {
          filterParts.push(`SO-${sanitizeFilenamePart(soNums[0])}`);
        } else if (soNums && soNums.length > 1) {
          filterParts.push(`${soNums.length}-SOs`);
        }
        if (poNums?.length === 1) {
          filterParts.push(`PO-${sanitizeFilenamePart(poNums[0])}`);
        } else if (poNums && poNums.length > 1) {
          filterParts.push(`${poNums.length}-POs`);
        }
        const filterSuffix =
          filterParts.length > 0 ? `-${filterParts.join('-')}` : '';

        const baseName = `${module}-audit-report${filterSuffix}-${dateStr}`;

        if (data.exports.csv) {
          const blob = new Blob([data.exports.csv], {
            type: 'text/csv;charset=utf-8;',
          });
          triggerDownload(blob, `${baseName}.csv`);
        }

        if (data.exports.pdf) {
          downloadBase64PDF(data.exports.pdf, `${baseName}.pdf`);
        }
      },
    }),
  );
}

export type { ExportFormat };
